// src/api/services.ts
import api from "./client";

export interface Service {
  service_id: string;
  service_name: string;
  description: string;
  ai_recommendation_notes?: string | null;
  duration_minutes: number;
  price: number;

  // Optional category info
  category_id?: string | null;
  category_name?: string | null;

  // Optional staff relations
  staff_ids?: string[];
  staff_members?: {
    staff_id: string;
    full_name: string;
  }[];
}

export interface SuggestedAiNotesDraft {
  ai_recommendation_notes: string;
  warnings: string[];
  source_fields: string[];
}

// ✅ Fetch all services
export const fetchServices = async (): Promise<Service[]> => {
  try {
    const res = await api.get("/manageBusinessServices");
    return (res.data || []) as Service[];
  } catch (error) {
    console.error("🚨 Error fetching services:", error);
    return [];
  }
};

// ✅ DELETE a service
export const deleteService = async (serviceId: string) => {
  if (!serviceId) {
    console.error("🚨 Missing service_id in DELETE request.");
    alert("❌ Missing service_id. Cannot delete.");
    return;
  }

  try {
    const res = await api.delete("/manageBusinessServices", {
      data: { service_id: serviceId },
    });

    return res.data;
  } catch (error) {
    console.error("🚨 DELETE Request Failed:", error);
    alert("❌ Service deletion failed.");
    throw error;
  }
};

// ✅ UPDATE a service
export const updateService = async (serviceId: string, updatedFields: Partial<Service>) => {
  try {
    const res = await api.put(`/manageBusinessServices`, {
      service_id: serviceId,
      ...updatedFields,
    });

    return res.data;
  } catch (error) {
    console.error("🚨 UPDATE Request Failed:", error);
    alert("❌ Error updating service.");
    throw error;
  }
};

export const suggestAiNotes = async (
  service: Partial<Service>
): Promise<SuggestedAiNotesDraft> => {
  const res = await api.post("/suggestServiceAiNotes", service);
  return res.data as SuggestedAiNotesDraft;
};

// ✅ ADD a new service
export const addService = async (service: Omit<Service, "service_id">) => {
  try {
    const res = await api.post("/manageBusinessServices", service);
    return res.data;
  } catch (error) {
    console.error("🚨 ADD Service Request Failed:", error);
    alert("❌ Failed to add service.");
    throw error;
  }
};
